import { useCallback, useEffect, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/frontend-plugin-api';
import { apiError, type Tone } from '@internal/ui';

/**
 * What an incident breaks: production (`prod`), or the platform the developers work with: the
 * ephemeral envs, the CI of app-bobbee, or both.
 */
export type IncidentScope = 'prod' | 'envs' | 'ci' | 'both';

export interface IncidentUpdate {
  id: string;
  message: string;
  author: string;
  createdAt: string;
}

export interface PlatformIncident {
  id: string;
  scope: IncidentScope;
  title: string;
  message?: string;
  startedAt: string;
  declaredBy: string;
  resolvedAt?: string;
  resolvedBy?: string;
  latestUpdate?: IncidentUpdate;
}

export interface IncidentInput {
  scope: IncidentScope;
  title: string;
  message: string;
  startedAt: string;
}

export const SCOPE_LABELS: Record<IncidentScope, string> = {
  prod: 'Production',
  envs: 'Envs éphémères',
  ci: 'CI',
  both: 'CI et envs éphémères',
};

/** Production incidents and platform ones (CI, ephemeral envs) are always shown apart. */
export type IncidentKind = 'prod' | 'platform';
export const kindOf = (scope: IncidentScope): IncidentKind =>
  scope === 'prod' ? 'prod' : 'platform';
export const KIND_LABELS: Record<IncidentKind, string> = {
  prod: 'Production',
  platform: 'CI et envs éphémères',
};
/** Production is red, the platform orange: the two never look alike. */
export const KIND_TONES: Record<IncidentKind, Tone> = {
  prod: 'error',
  platform: 'warning',
};

/** True when an incident of `scope` concerns one of `scopes`; `both` covers the CI and the envs. */
export function concerns(
  scope: IncidentScope,
  scopes: IncidentScope[],
): boolean {
  if (scopes.includes(scope)) return true;
  if (scope === 'both') return scopes.includes('envs') || scopes.includes('ci');
  return (scope === 'envs' || scope === 'ci') && scopes.includes('both');
}

/** « 10:42 » today, « 29/10 18:05 » before. */
export function clock(iso: string, now = Date.now()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (at.toDateString() === new Date(now).toDateString()) return time;
  return `${at.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
  })} ${time}`;
}

/** `user:default/jdubois_isagri.fr` → `jdubois`: users have no catalog entity to show a name. */
export const authorName = (userRef: string) =>
  userRef.replace(/^user:[^/]+\//, '').replace(/_[^_]*$/, '');

const BASE = 'plugin://platform-orchestrator/incidents';
const REFRESH_MS = 60_000;

/**
 * GET `path` of the incidents API, refreshed every minute while the tab is visible. A failed
 * refresh keeps the last value: incidents are a hint, never a reason to break a page.
 */
function useIncidentsFetch<T>(path: string | undefined) {
  const fetchApi = useApi(fetchApiRef);
  const [value, setValue] = useState<T>();
  const [error, setError] = useState<Error>();

  const reload = useCallback(async () => {
    if (path === undefined) return;
    try {
      const res = await fetchApi.fetch(`${BASE}${path}`);
      if (!res.ok) throw await apiError(res);
      setValue((await res.json()) as T);
      setError(undefined);
    } catch (e) {
      setError(e as Error);
    }
  }, [fetchApi, path]);

  useEffect(() => {
    reload();
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') reload();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reload]);

  return { value, error, reload };
}

/** Open incidents and those resolved in the last 2 hours: what the banners show. */
export function usePlatformIncidents(): PlatformIncident[] {
  const { value } = useIncidentsFetch<{ incidents?: PlatformIncident[] }>('');
  return Array.isArray(value?.incidents) ? value!.incidents : [];
}

/** Every incident, newest first (latest 100): the Incidents page. */
export function useIncidentHistory() {
  const { value, error, reload } = useIncidentsFetch<{
    incidents: PlatformIncident[];
  }>('/history');
  return { incidents: value?.incidents, error, reload };
}

/** One incident with its thread of updates, oldest first. */
export function useIncident(id: string | undefined) {
  const { value, error, reload } = useIncidentsFetch<
    PlatformIncident & { updates: IncidentUpdate[] }
  >(id ? `/${encodeURIComponent(id)}` : undefined);
  return { incident: value, error, reload };
}

/** Writes of the incident managers; each throws the backend's message. */
export function useIncidentActions() {
  const fetchApi = useApi(fetchApiRef);
  const send = useCallback(
    async (path: string, method: string, body?: unknown) => {
      const res = await fetchApi.fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    [fetchApi],
  );
  return {
    /** Declares (no id) or edits an incident; resolves to the incident. */
    save: (input: IncidentInput, id?: string): Promise<PlatformIncident> =>
      id ? send(`/${id}`, 'PATCH', input) : send('', 'POST', input),
    post: (id: string, message: string) =>
      send(`/${id}/updates`, 'POST', { message }),
    resolve: (id: string, note: string) =>
      send(`/${id}/resolve`, 'POST', note.trim() ? { note } : {}),
  };
}
